import * as dagre from 'dagre';
import type { Ast } from './freeDslParser';
import type { MetamodelContext } from './metamodelContext';

// Default dimensions keyed by DSL keyword (what the parser stores in shape.type).
// 'database' is the explicit factory key (creates CYLINDER with isDatabase:true).
// ELEMENT_TYPES.DATABASE = 'database_shape' but users don't type that in DSL.
const DEFAULT_SIZE: Record<string, { w: number; h: number }> = {
  rectangle:        { w: 140, h: 80  },
  circle:           { w: 60,  h: 60  },
  ellipse:          { w: 120, h: 80  },
  diamond:          { w: 100, h: 100 },
  cylinder:         { w: 80,  h: 100 },
  database:         { w: 80,  h: 100 },
  cloud:            { w: 120, h: 80  },
  document:         { w: 100, h: 80  },
  queue:            { w: 100, h: 60  },
  actor:            { w: 60,  h: 100 },
  component:        { w: 140, h: 80  },
  package:          { w: 120, h: 80  },
  use_case:         { w: 140, h: 80  },
  interface:        { w: 40,  h: 40  },
  process:          { w: 100, h: 60  },
  decision:         { w: 100, h: 80  },
  terminator:       { w: 120, h: 50  },
  preparation:      { w: 120, h: 60  },
  manual_input:     { w: 120, h: 60  },
  manual_operation: { w: 120, h: 60  },
  display:          { w: 120, h: 80  },
  data:             { w: 120, h: 60  },
  internal_storage: { w: 120, h: 80  },
  l_rect:           { w: 100, h: 100 },
  u_rect:           { w: 100, h: 100 },
  trapezoid:        { w: 120, h: 60  },
  inv_trapezoid:    { w: 120, h: 60  },
  step:             { w: 100, h: 60  },
  callout:          { w: 100, h: 80  },
  loop_limit:       { w: 120, h: 60  },
  storage:          { w: 120, h: 80  },
  triangle:         { w: 100, h: 100 },
  parallelogram:    { w: 120, h: 60  },
  rounded_rect:     { w: 120, h: 60  },
};

const FALLBACK_SIZE = { w: 120, h: 60 };

const DASH: Record<string, string | undefined> = {
  solid:  undefined,
  dashed: '8,4',
  dotted: '2,4',
};

type Pt = { x: number; y: number };
type ElementFactoryLike = Record<string, any>;

export function layoutFreeDsl(ast: Ast, factory: ElementFactoryLike, ctx?: MetamodelContext): any[] {
  // Build a dagre graph for layout
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 120 });
  g.setDefaultEdgeLabel(() => ({}));

  const sizeOf = (type: string): { w: number; h: number } => {
    const def = ctx?.keywords.get(type);
    if (def) {
      const base = DEFAULT_SIZE[def.baseType] ?? FALLBACK_SIZE;
      return { w: def.width ?? base.w, h: def.height ?? base.h };
    }
    return DEFAULT_SIZE[type] ?? FALLBACK_SIZE;
  };

  for (const s of ast.shapes) {
    const { w, h } = sizeOf(s.type);
    // Reserve extra vertical space for the actor's below-label
    const dagreH = s.type === 'actor' ? h + 30 : h;
    g.setNode(s.id, { width: w, height: dagreH });
  }

  const knownIds = new Set(ast.shapes.map(s => s.id));
  for (const a of ast.arrows) {
    if (knownIds.has(a.fromId) && knownIds.has(a.toId)) {
      g.setEdge(a.fromId, a.toId);
    }
  }

  try {
    dagre.layout(g);
  } catch {
    layoutGridFallback(g, ast);
  }

  const shapesById = new Map<string, any>();
  const result: any[] = [];

  for (const s of ast.shapes) {
    const node = g.node(s.id);
    const { w, h } = sizeOf(s.type);

    // dagre node.x/y are centers; convert to top-left
    const cx = node?.x ?? (w / 2);
    const cy = node?.y ?? (h / 2);
    const x = cx - w / 2;
    const y = cy - h / 2;

    // Resolve keyword definition from metamodel context
    const def = ctx?.keywords.get(s.type);
    const effectiveType = def?.baseType ?? s.type;

    let el: any;

    if (effectiveType === 'circle') {
      // circle factory: (x, y, radius, props)
      const radius = w / 2;
      el = factory.circle(x, y, radius, { content: s.label });
      el.x = x;
      el.y = y;
    } else if (effectiveType === 'database') {
      el = factory.database(x, y, w, h, { content: s.label });
    } else if (effectiveType === 'rectangle') {
      el = factory.rectangle(x, y, w, h, { content: s.label });
    } else if (effectiveType === 'actor') {
      // Actor stickman — do NOT set content here; label goes below as separate text
      const actorFn = factory['actor'];
      el = actorFn(x, y);
      el.width  = w;
      el.height = h;
      el.x = x;
      el.y = y;
    } else {
      // All other bracket-keyed factories: call (x, y) then set geometry
      const factoryFn: ((x: number, y: number) => any) | undefined = factory[effectiveType];
      if (factoryFn) {
        el = factoryFn(x, y);
        el.width  = w;
        el.height = h;
      } else {
        // Fallback to rectangle for unknown types (e.g. 'erd-class')
        el = factory.rectangle(x, y, w, h, { content: s.label });
      }
      el.x = x;
      el.y = y;
      if (s.label) el.content = s.label;
    }

    // Apply metamodel style overrides
    if (def) {
      if (def.fill         != null) el.fill         = def.fill;
      if (def.stroke       != null) el.stroke       = def.stroke;
      if (def.strokeWidth  != null) el.strokeWidth  = def.strokeWidth;
      if (def.borderRadius != null) el.borderRadius = def.borderRadius;
      if (def.iconDataUrl  != null) el.iconDataUrl  = def.iconDataUrl;
      if (def.width        != null) el.width        = def.width;
      if (def.height       != null) el.height       = def.height;
    }

    el._dslId = s.id;
    shapesById.set(s.id, el);
    result.push(el);

    // Actor label: separate text element below the stickman figure
    if (effectiveType === 'actor' && s.label) {
      const lblEl = factory.text(x + w / 2, y + h + 16, s.label);
      result.push(lblEl);
    }
  }

  for (const a of ast.arrows) {
    const fromEl = shapesById.get(a.fromId);
    const toEl   = shapesById.get(a.toId);
    if (!fromEl || !toEl) continue;

    const fromW = fromEl.width  ?? (fromEl.radius != null ? fromEl.radius * 2 : FALLBACK_SIZE.w);
    const fromH = fromEl.height ?? (fromEl.radius != null ? fromEl.radius * 2 : FALLBACK_SIZE.h);
    const toW   = toEl.width    ?? (toEl.radius   != null ? toEl.radius   * 2 : FALLBACK_SIZE.w);
    const toH   = toEl.height   ?? (toEl.radius   != null ? toEl.radius   * 2 : FALLBACK_SIZE.h);

    const fromCx = fromEl.x + fromW / 2;
    const toCx   = toEl.x   + toW   / 2;

    // Detect backward direction (source is to the right of target in LR layout)
    const goingLeft = fromCx > toCx;

    let startX: number, startY: number, endX: number, endY: number;
    let pathPoints: Pt[] | null = null;
    let startSide: string, endSide: string;

    if (goingLeft) {
      // Back-arrow: route below both shapes so it doesn't cross forward arrows
      const routeY = Math.max(fromEl.y + fromH, toEl.y + toH) + 45;
      startX = fromCx;
      startY = fromEl.y + fromH;
      endX   = toCx;
      endY   = toEl.y + toH;
      pathPoints = [
        { x: startX, y: startY },
        { x: startX, y: routeY },
        { x: endX,   y: routeY },
        { x: endX,   y: endY   },
      ];
      startSide = 'bottom';
      endSide   = 'bottom';
    } else {
      // Forward arrow: right edge of source → left edge of target at mid-height
      startX = fromEl.x + fromW;
      startY = fromEl.y + fromH / 2;
      endX   = toEl.x;
      endY   = toEl.y + toH   / 2;
      startSide = 'right';
      endSide   = 'left';
    }

    const arrowEl = factory.arrow(
      startX, startY,
      endX,   endY,
      { elementId: fromEl.id, side: startSide },
      { elementId: toEl.id,   side: endSide   },
    );

    if (pathPoints) {
      arrowEl.pathPoints = pathPoints;
    }
    if (DASH[a.style] !== undefined) {
      arrowEl.strokeDasharray = DASH[a.style];
    }
    arrowEl.bidir = a.bidir;
    result.push(arrowEl);

    if (a.label) {
      let labelX: number, labelY: number;
      if (pathPoints) {
        // Place label along the horizontal segment below the shapes
        labelX = (startX + endX) / 2;
        labelY = pathPoints[1].y - 12;
      } else {
        labelX = (startX + endX) / 2;
        labelY = (startY + endY) / 2 - 10;
      }
      const labelEl = factory.text(labelX, labelY, a.label);
      labelEl.attachedToElement = arrowEl.id;
      result.push(labelEl);
    }
  }

  return result;
}

function layoutGridFallback(g: any, ast: Ast): void {
  const cols = 4;
  const cellW = 220;
  const cellH = 160;
  ast.shapes.forEach((s, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const existing = g.node(s.id) || {};
    g.setNode(s.id, {
      ...existing,
      x: col * cellW + cellW / 2,
      y: row * cellH + cellH / 2,
    });
  });
}
